import React from "react";
import { Mail, MapPin } from "lucide-react";
import { motion } from "framer-motion";
import pic1 from "../../../assets/images/outbound calls.jpg";
import { useNavigate } from "react-router-dom";
const OutboundCalls = () => {
  const navigate = useNavigate();
  return (
    <div className="w-full" style={{ marginTop: "80px" }}>
      <motion.header
        className="bg-blue-900 text-white py-16 text-center shadow-lg rounded-lg"
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className="bg-gradient-to-r from-blue-50 to-indigo-100 py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="text-white">
                  📞 Outbound Call Services for{" "}
                </span>
                <span className="text-blue-600"> Medical Practices</span>
              </h1>
              <p className="mt-4 font-bold">
                Enhance Patient Experience & Streamline Operations with Right On
                Time Services
              </p>
              <p className="text-white mt-4 mb-4">
                At Right On Time Services, we specialize in providing
                comprehensive outbound call services that help you stay
                connected with your patients, improve their experience, and
                optimize your practice’s operations.
              </p>
              <button
                onClick={() => navigate("/bookmeeting")}
                style={{ color: "black" }}
                className="mt-4 bg-white text-blue-700 px-6 py-3 font-bold rounded-full hover:bg-blue-100 transition"
              >
                📞 Partner with Us
              </button>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Our Outbound Call Services */}
      <section className="py-16 text-center bg-white">
        <h2 className="text-3xl font-bold text-blue-900">
          📅 Our Outbound Call Services
        </h2>
        <p className="mt-4">
          We offer a wide range of outbound call services tailored to your
          practice’s needs, including:
        </p>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-gray-100">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-6 px-8">
          {[
            {
              title: "📅 Appointment Reminders",
              description:
                "Minimize no-shows and optimize your schedule with personalized appointment reminder calls. Our courteous team ensures that:",
              points: [
                "Patients confirm their date and time.",
                "All their questions are answered.",
                "They receive necessary instructions before the appointment.",
              ],
              title2:
                "By ensuring clear communication, our service helps reduce cancellations, increase patient satisfaction, and boost revenue retention.",
            },
            {
              title: "🩺 Follow-Up Calls",
              description:
                "Our follow-up calls help patients continue their care journey by:",
              points: [
                "Checking on patient progress after appointments.",
                "Addressing any concerns or questions.",
                "Offering additional care guidance when necessary.",
              ],
              title2:
                "These calls are especially important for patients with chronic conditions or ongoing treatments, showing your commitment to their health and improving their outcomes.",
            },
            {
              title: "📝 Patient Surveys",
              description:
                "Gather valuable patient feedback through our survey calls, which help your practice:",
              points: [
                "Measure patient satisfaction.",
                "Identify areas for improvement",
                "Strengthen  patient relationships",
              ],
              title2:
                "Our team will collect feedback on staff friendliness, wait times, and overall service, enabling you to continuously improve your care delivery.",
            },
            {
              title: "💊 Prescription Refill Calls",
              description:
                "Ensure medication adherence and enhance patient outcomes with our prescription refill reminder calls. Our trained staff:",
              points: [
                "Contacts patients for timely refills.",
                "Ensures dosage accuracy",
                "Answers medication-related queries",
              ],
              title2:
                "This service is especially beneficial for patients managing complex medical conditions, helping them stay on track with their treatment.",
            },
            {
              title: "👋 New Patient Welcome Calls",
              description:
                "Make a lasting first impression with our new patient welcome calls, which include:",
              points: [
                "Introduce your practice and services",
                "Provide essential information for a smooth start.",
                "Answering initial patient inquiries.",
              ],
              title2:
                "Welcoming new patients warmly builds trust, reduces first-visit anxiety, and sets the tone for a long-term relationship.",
            },
            {
              title: "💳 Payment Reminder Calls",
              description:
                "Maintain a healthy cash flow with our payment reminder services. Our team:",
              points: [
                "Notifies patients of outstanding balances",
                "Answers payment-related questions",
                "Facilitates easy payment options",
              ],
              title2:
                "Timely payment reminders help improve collections, reduce financial stress, and eliminate the need for collection agencies.",
            },
          ].map((service, index) => (
            <motion.div
              key={index}
              whileHover={{ scale: 1.05 }}
              className="p-6 bg-white rounded-lg shadow-md"
            >
              <h3 className="text-xl font-bold text-blue-900">
                {service.title}
              </h3>
              <p className="mt-2 text-gray-700">{service.description}</p>
              <ul className="mt-3 space-y-2 text-gray-700">
                {service.points.map((point, i) => (
                  <li key={i}>● {point}</li>
                ))}
              </ul>
              <p className="mt-2 text-gray-700">{service.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900">
            🌟 Why Choose Right On Time Services?
          </h2>
          <p className="text-lg text-gray-700 mt-4">
            Here’s why Right On Time Services is the ideal choice for your
            practice:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
            {[
              "Tailored Solutions: Customizable services to meet the unique needs of your practice.",
              "Experienced Professionals: Skilled staff trained in medical communication and patient engagement.",
              "HIPAA Compliant: We prioritize patient privacy and data security, ensuring complete confidentiality.",
              "Improved Patient Satisfaction: Enhance relationships through consistent and caring communication.",
              "Streamlined Practice Management: Reduce administrative burdens, allowing you to focus on delivering quality care.",
            ].map((item, index) => (
              <motion.div
                key={index}
                whileHover={{ scale: 1.05 }}
                className="bg-gray-100 p-6 rounded-lg shadow-md"
              >
                <h3 className="text-xl font-bold text-blue-500">{item}</h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <motion.section
        className="py-16 text-center bg-gray-100"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
        data-aos="fade-up"
      >
        <section
          id="get-started-outbound"
          className="py-24 bg-white px-4 md:px-12 mb-4"
        >
          <div className="container mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 items-center shadow-2xl rounded-2xl p-8 md:p-12">
            {/* Left Side - Centered Content */}
            <div className="flex flex-col justify-center items-center text-left">
              <h2 className="text-4xl font-extrabold text-blue-800 mb-4">
                🚀 Let’s Get Started
              </h2>
              <p className="text-base text-gray-700 mb-4 max-w-xl">
                Partner with Right On Time Services for Reliable, Professional,
                and HIPAA-Compliant Outbound Call Solutions
              </p>
              <p className="text-base text-gray-700 mb-6 max-w-xl">
                Contact us today to learn how our outbound call services can
                help your medical practice improve patient engagement and
                operational efficiency.
              </p>
              <div className="text-base text-gray-700 space-y-2">
                <p>
                  📧 <strong>Email:</strong> info@rightontime.biz
                </p>
                <p>
                  📍 <strong>Address:</strong> Al-Rehman Chamber, Fazal e Haq
                  Road, G 7/3 Blue Area, Islamabad, 44000 Rawalpindi.
                </p>
                <p>
                  📞 <strong>Phone:</strong> 03394017907
                </p>
              </div>
            </div>

            {/* Right Side - Centered Image */}
            <div className="flex justify-center items-center">
              <img
                src={pic1}
                alt="Outbound Call Support"
                className="w-auto h-auto max-h-[400px] object-contain rounded-xl shadow-lg"
              />
            </div>
          </div>
        </section>
      </motion.section>

      <motion.section
        className="py-8 text-center bg-gray-100"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
        data-aos="fade-up"
      >
        <h2 className="text-3xl font-bold text-blue-900">
          🌟 Ready to Enhance Patient Engagement?
        </h2>
        <h5 className="mt-4 text-blue-900 mb-8">
          Let’s Start Together – Transform your patient communication with our
          expert outbound call services.
        </h5>
      </motion.section>
    </div>
  );
};

export default OutboundCalls;
